'use strict';

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import qs from 'qs';
import Modal from 'react-modal';
import moment from 'moment';

import ImgResized from '@components/Widgets/ImgResized.react';
import CoverImagePicker from '@components/Widgets/CoverImagePicker.react';
import Dropdown from '@pro/components/Widgets/Dropdown.react';
import Analytics from '@utils/Analytics';
import UserStore from '@stores/UserStore';
import { extractCoverImages } from '@utils/CoverImage';
import { useModalControls } from '../contexts';
import { useUIState } from '../contexts';

import defaultImages from '@app/tables/cover-images.js';

import './PlanToPdfButton.scss';
import '@components/Widgets/CoverImagePicker.scss';

const PlanToPdfButton = ({ button, classes, plan, patient }) => {
    const { showUpgradeForm } = useModalControls();
    const { router } = useUIState();

    // Initialize state with values derived from props
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [user, setUser] = useState(() => UserStore.getUser());
    const [canPrintPlans, setCanPrintPlans] = useState(() => {
        const currentUser = UserStore.getUser();
        return currentUser.capabilities.print_plan || false;
    });
    
    // Initialize params and cover_images based on props
    const [params, setParams] = useState(() => {
        const initialParams = {
            plan_uuid: plan.uuid,
        };

        if (patient) {
            initialParams.patient_uuid = patient.uuid;

            if (patient.cover_image) {
                const { selectedCoverImage } = extractCoverImages(patient.cover_image);
                if (selectedCoverImage) {
                    initialParams.cover_image = selectedCoverImage;
                }
            }

            const currentUser = UserStore.getUser();
            const { practice } = currentUser;

            if (practice && practice.cover_image) {
                const { selectedCoverImage } = extractCoverImages(practice.cover_image);
                if (selectedCoverImage && !initialParams.cover_image) {
                    initialParams.cover_image = selectedCoverImage;
                }
            }

            if (patient.hide_nutrition) {
                initialParams.hide_nutrition = 1;
            }
        }

        return initialParams;
    });

    const [coverImages, setCoverImages] = useState(() => {
        let images = defaultImages.slice();

        if (patient && patient.cover_image) {
            const { images: patientImages } = extractCoverImages(patient.cover_image);
            patientImages.forEach(url => {
                if (url && !images.includes(url)) {
                    images.push(url);
                }
            });
        }

        const currentUser = UserStore.getUser();
        const { practice } = currentUser;

        if (practice && practice.cover_image) {
            const { images: practiceImages } = extractCoverImages(practice.cover_image);
            practiceImages.forEach(url => {
                if (url && !images.includes(url)) {
                    images.push(url);
                }
            });
        }

        return images;
    });

    // Set up UserStore listener
    useEffect(() => {
        const onUserStoreChange = () => {
            const updatedUser = UserStore.getUser();
            const updatedCanPrintPlans = updatedUser.capabilities.print_plan || false;

            setUser(updatedUser);
            setCanPrintPlans(updatedCanPrintPlans);
        };

        UserStore.addChangeListener(onUserStoreChange);

        return () => {
            UserStore.removeChangeListener(onUserStoreChange);
        };
    }, []);

    const toggleParam = useCallback((key) => {
        setParams(prevParams => {
            const newParams = { ...prevParams };
            if (newParams[key]) {
                delete newParams[key];
            } else {
                newParams[key] = 1;
            }
            return newParams;
        });
    }, []);

    const setIngredientMode = useCallback((mode) => {
        setParams(prevParams => {
            const newParams = { ...prevParams };
            if (!mode) {
                delete newParams.ingr_mode;
            } else {
                newParams.ingr_mode = mode;
            }
            return newParams;
        });
    }, []);

    const trackPdfDownload = useCallback(() => {
        Analytics.downloadMealPlanPDF({
            'Patient UUID': params.patient_uuid
        });
    }, [params.patient_uuid]);

    const openModal = useCallback(() => {
        setIsModalOpen(true);
    }, []);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const setCoverImage = useCallback((url) => {
        setParams(prevParams => ({
            ...prevParams,
            cover_image: url
        }));
    }, []);

    const onChangeCoverImage = useCallback(({ url }) => {
        setCoverImages(prevImages => {
            const updatedImages = [...prevImages];
            if (url && !updatedImages.includes(url)) {
                updatedImages.push(url);
            }
            return updatedImages;
        });
        
        setParams(prevParams => ({
            ...prevParams,
            cover_image: url
        }));
    }, []);

    const subscriptionRequired = useCallback(() => {
        showUpgradeForm({feature: 'print_plan'});
    }, [showUpgradeForm]);

    const renderPdfDialog = () => {
        if (!isModalOpen) {
            return null;
        }

        const using = params.cover_image || coverImages[0];

        return (
            <Modal isOpen={true}
                onRequestClose={closeModal}
                contentLabel="Meal Plan Print Options"
                className="el-modal el-modal2 el-modal2-centered"
                overlayClassName="el-modal-overlay"
                closeTimeoutMS={250}>

                <section className="el-modal-container el-modal2-container plan-to-pdf-container">
                    <header>
                        <h2>Meal Plan Print Options</h2>

                        <button className="el-modal-close-x" onClick={closeModal}>
                            <i className="icon-close-x" />
                            <span className="assistive-text">Close Modal</span>
                        </button>
                    </header>

                    <div className="el-modal-body-container el-modal2-body-container">
                        <div className="plan-to-pdf-form">
                            <h3>Include:</h3>
                            <div className="toggle-group">
                                <button className="toggle-btn" data-selected={params.hide_cover ? false : true} onClick={() => toggleParam('hide_cover')}>
                                    Cover Page
                                </button>

                                <button className="toggle-btn" data-selected={params.hide_care ? false : true} onClick={() => toggleParam('hide_care')}>
                                    Care Plan
                                </button>

                                <button className="toggle-btn" data-selected={params.hide_grocery ? false : true} onClick={() => toggleParam('hide_grocery')}>
                                    Grocery List
                                </button>

                                <button className="toggle-btn" data-selected={params.hide_grid ? false : true} onClick={() => toggleParam('hide_grid')}>
                                    Meal Schedule Grid
                                </button>

                                <button className="toggle-btn" data-selected={params.hide_photos ? false : true} onClick={() => toggleParam('hide_photos')}>
                                    Recipe Photos
                                </button>

                                <button className="toggle-btn" data-selected={params.hide_dtl ? false : true} onClick={() => toggleParam('hide_dtl')}>
                                    Day in the Life
                                </button>
                            </div>

                            <h3>Ingredients Options:</h3>
                            <div className="toggle-group">
                                <button className="toggle-btn radio-btn" data-selected={params.ingr_mode == 'default' || !params.ingr_mode} onClick={() => setIngredientMode()}>
                                    Cups
                                </button>

                                <button className="toggle-btn radio-btn" data-selected={params.ingr_mode == 'grams'} onClick={() => setIngredientMode('grams')}>
                                    Grams
                                </button>

                                <button className="toggle-btn radio-btn" data-selected={params.ingr_mode == 'ounces'} onClick={() => setIngredientMode('ounces')}>
                                    Ounces
                                </button>
                            </div>

                            <h3>Cover Image:</h3>
                            <CoverImagePicker
                                selectedImage={using}
                                availableImages={coverImages}
                                onImageSelect={setCoverImage}
                                onImageUpload={onChangeCoverImage}
                                uploadButtonText="Upload Your Own Cover Photo"
                                uploadButtonClassName="el-pro-raspberry-btn el-pro-medium-btn"
                                showDimensions={false}
                                uploadOption={true}
                            />

                        </div>
                    </div>

                    <footer>
                        <button className="el-modal-cancel-btn" onClick={closeModal}>Close</button>
                        {!canPrintPlans ?
                            <button className="el-modal-ok-btn" onClick={subscriptionRequired}>Generate <i className="icon-lock" /></button>
                        : null}
                        {canPrintPlans ?
                            <a target="_blank" onClick={trackPdfDownload} href={'/plan-to-pdf?' + qs.stringify(params)}>
                                <button className="el-modal-ok-btn">Generate</button>
                            </a>
                        : null}
                    </footer>
                </section>
            </Modal>
        );
    };

    return (
        <>
            <button className={classes || "ok-btn"} onClick={openModal}>{button || <i className="icon-print" />}</button>
            {renderPdfDialog()}
        </>
    );
};

PlanToPdfButton.propTypes = {
    button: PropTypes.node,
    classes: PropTypes.string,
    plan: PropTypes.object.isRequired,
    patient: PropTypes.object,
};

export default PlanToPdfButton;